<template>
  <div class="table-responsive">
    <table class="table align-middle main-table">
      <thead>
        <tr class="table-light text-default">
          <th v-for="header in headers" :key="header.value" :style="{ 'width': header.width || 'auto', 'text-align': header.align || 'left' }">
            {{ header.text }}
          </th>
        </tr>
      </thead>
      <tbody v-if="loading">
        <tr>
          <td :colspan="headers.length" class="text-center p-5">
            <div class="spinner-border text-primary" role="status">
              <span class="visually-hidden">Loading...</span>
            </div>
          </td>
        </tr>
      </tbody>
      <tbody v-else-if="policies.length === 0 && showNoPoliciesMessage">
        <tr><td :colspan="headers.length" class="text-center p-4 text-muted">No policies to display.</td></tr>
      </tbody>
      <tbody v-else v-for="policy in policies" :key="policy.ID" class="align-middle text-default">
        <tr>
          <td style="width:10%;">
            <span class="badge policy-badge"
                  :class="['policy--' + policy.status.toLowerCase(), { 'cursor-pointer': !policy.archived }]"
                  :style="{ cursor: policy.archived ? 'not-allowed' : 'pointer' }"
                  @click="!policy.archived && $emit('review', policy)"
                  v-tooltip.top="policy.archived ? 'Archived' : 'Click to review policy'">
              {{ formatStatus(policy.status) }}
            </span>
          </td>

          <td style="width:15%;" :class="{ 'cursor-pointer': !policy.archived }" @click="!policy.archived && $emit('edit', policy)">
            <span>{{ policy.name }}</span>
          </td>

          <td class="text-center" style="width:5%;" :class="{ 'cursor-pointer': !policy.archived }" @click="!policy.archived && $emit('edit', policy)">{{ policy.version }}</td>

          <td class="text-center" style="width:10%;" :class="{ 'cursor-pointer': !policy.archived }" @click="!policy.archived && $emit('edit', policy)">{{ formatDate(policy.date_uploaded) }}</td>

          <td class="text-center" style="width:10%;" :class="{ 'cursor-pointer': !policy.archived }" @click="!policy.archived && $emit('edit', policy)">{{ formatDate(policy.review_date) }}</td>

          <td style="width:15%;">
            <select class="form-select table-select shadow-none"
                    :value="policy.owner"
                    :disabled="policy.archived"
                    @change="$emit('set-owner', { policy, ownerId: parseInt($event.target.value) })">
              <option :value="0"></option>
              <option v-for="user in users" :key="user.id" :value="user.id">{{ user.first_name + " " + user.last_name }}</option>
            </select>
          </td>

          <td style="width:5%; text-align: center;">
            <div class="styled-checkbox d-inline-block">
              <input
                class="form-check-input"
                type="checkbox"
                :id="'policy-req-' + policy.ID"
                :checked="policy.required"
                :disabled="policy.archived"
                @change="toggleRequirement(policy)" />
              <label
                class="form-check-label"
                :for="'policy-req-' + policy.ID">
              </label>
            </div>
          </td>

          <td style="width:20%;">
            <select class="form-select table-select shadow-none" :value="policy.training_videos[0]?.ID || 0"
                    :disabled="policy.archived"
                    @change="$emit('set-training-link', { policy, videoId: parseInt($event.target.value) })">
              <option :value="0"></option>
              <option v-for="videoOption in videoIDs" :key="videoOption.value" :value="videoOption.value">{{ videoOption.label }}</option>
            </select>
          </td>

          <td style="width:10%; text-align: center;">
            <div class="d-flex align-items-center">
              <archive-switch :policy="policy" @archive="$emit('archive', $event)" />
              <button type="button" class="btn btn-circle ms-1" @click="$emit('view-file', policy)" v-tooltip.top="'Show policy'">
                <i class="fa fa-file-contract"></i>
              </button>
              <button type="button" class="btn btn-circle ms-1"
                      :disabled="policy.archived"
                      @click="$emit('edit', policy)"
                      v-tooltip.top="'Edit Policy'">
                <i class="fa fa-pen"></i>
              </button>
              <button
                type="button"
                class="btn btn-circle ms-1"
                v-tooltip.top="'Send email reminder'"
                :disabled="!policy.required || loadingPolicyIds.has(policy.ID) || policy.archived"
                @click="$emit('remind', policy)"
              >
                <span v-if="loadingPolicyIds.has(policy.ID)">
                  <i class="fa fa-spinner fa-spin"></i>
                </span>
                <span v-else>
                  <i class="fa fa-calendar"></i>
                </span>
              </button>
              <button type="button" class="btn btn-circle btn-circle-danger ms-1" @click="$emit('delete', policy)" v-tooltip.top="'Delete Policy'">
                <i class="fas fa-times"></i>
              </button>
            </div>
          </td>
        </tr>
      </tbody>
      <tfoot v-if="!loading && policies.length > 0">
        <tr>
          <td :colspan="headers.length" class="text-center">
            <div class="d-inline pagination">
              <a @click="$emit('page-change', pagination.page - 1)"
                 class="pagination-arrow"
                 :class="{ 'disabled': pagination.page === 1 }">
                <i class="fa fa-chevron-left"></i>
              </a>
              <span class="pagination-label">
                <strong>
                  <span>{{ paginationLabel }} of {{ pagination.totalItems }}</span>
                </strong>
              </span>
              <a @click="$emit('page-change', pagination.page + 1)"
                 class="pagination-arrow"
                 :class="{ 'disabled': pagination.page === pagination.totalPages || pagination.totalItems === 0 }">
                <i class="fa fa-chevron-right"></i>
              </a>
            </div>
          </td>
        </tr>
      </tfoot>
    </table>
  </div>
</template>

<script>
import moment from "moment";
import ArchiveSwitch from '@/components/policies/ArchiveSwitch.vue';

export default {
  name: "PolicyLibraryTable",
  components: {
    ArchiveSwitch,
  },
  props: {
    policies: { type: Array, required: true },
    users: { type: Array, required: true },
    videoIDs: { type: Array, required: true },
    loadingPolicyIds: { type: Set, required: true },
    pagination: { type: Object, required: true },
    showNoPoliciesMessage: { type: Boolean, default: true },
    loading: { type: Boolean, default: false },
  },
  emits: [
    'review', 'edit', 'set-owner', 'set-requirement', 'set-training-link',
    'archive', 'view-file', 'remind', 'delete',
    'page-change',
  ],
  data() {
    return {
      headers: [
        { text: "Status", value: "status", width: "10%" },
        { text: "Policy Name", value: "policy", width: "15%" },
        { text: "Version", value: "version", width: "5%", align: "center" },
        { text: "Date Uploaded", value: "dateUploaded", width: "10%", align: "center" },
        { text: "Review Date", value: "reviewDate", width: "10%", align: "center" },
        { text: "Owner", value: "owner", width: "15%" },
        { text: "Attestation Req.", value: "attestationRequired", width: "5%", align: "center" },
        { text: "Link to Training Video", value: "linkToTrainingVideo", width: "20%" },
        { text: "Actions", value: "action", width: "10%", align: "center" },
      ],
    }
  },
  computed: {
    paginationLabel() {
      const start = Math.min((this.pagination.page - 1) * this.pagination.pageSize + 1, this.pagination.totalItems);
      const end = Math.min((this.pagination.page * this.pagination.pageSize), this.pagination.totalItems);
      return `${start} - ${end}`;
    },
  },
  methods: {
    toggleRequirement(policy) {
      policy.required = !policy.required;
      this.$emit('set-requirement', policy);
    },
    formatDate(date) {
      if (!date) return "";
      return moment(date).format("DD-MMM-YYYY");
    },
    formatStatus(status) {
      const statusMap = {
        current: "Current",
        review_required: "Review Required",
        review_overdue: "Review Overdue",
      };
      return statusMap[status] || status;
    },
  }
}
</script>

<style scoped>
.pagination-arrow {
  margin: 0 8px;
  color: #00a0c1;
  cursor: pointer;
}
.pagination-arrow.disabled {
  color: #ccc;
  cursor: not-allowed;
}
.pagination-label {
  display: inline-block;
  min-width: 120px;
}
.cursor-pointer {
  cursor: pointer;
}
.policy--current { background-color: #00A54F; color: #fff; }
.policy--review_required { background-color: #F3994D; color: #fff; }
.policy--review_overdue { background-color: #EB1C24; color: #fff; }
.policy-badge { width: 127px; text-align: center; display: inline-block; height: 24px; font-size: 13px; font-weight: 900; }
.styled-checkbox { position: relative; width: 25px; height: 25px; }
.styled-checkbox input { position: absolute; opacity: 0; }
.styled-checkbox label {
  position: absolute;
  top: 0;
  left: 0;
  width: 25px;
  height: 25px;
  background-color: transparent;
  border: 2px solid #6c757d;
  border-radius: 1px;
  cursor: pointer;
}
.styled-checkbox input:checked+label { border-color: #198754; }
.styled-checkbox input:checked+label::before { content: '\2713'; position: absolute; top: 50%; left: 50%; transform: translate(-50%, -50%); font-size: 16px; color: #198754; font-weight: bold; }
.table-select { padding-left: 5px; cursor: pointer; border: none; }
.table-select:not(:hover) { background: none; }
.btn-circle { width: 30px; height: 30px; padding: 6px 0px; border-radius: 15px; text-align: center; font-size: 12px; line-height: 1.42857; border: 1px solid #ddd; background-color: #fff; }
.btn-circle:hover { background-color: #f8f9fa; }
.btn-circle-danger { border-color: #dc3545; color: #dc3545; }
.btn-circle-danger:hover { background-color: #dc3545; color: #fff; }
</style>